<script setup>
import { mapImports } from '#common/utils/importAll'

const mobileMenuImages = mapImports(
  import.meta.glob('~/assets/images/mobile-nav/*.svg', {
    eager: true,
  }),
)

const props = defineProps({
  menu: {
    type: Object,
    default: () => ({ items: [], text: 'default' }),
  },
})
const { menu } = toRefs(props)
const emit = defineEmits(['setActiveMenu', 'link-clicked'])
const { $auth } = useNuxtApp()
const layout = useLayout()
const loggedIn = computed(() => $auth.loggedIn)
const menuList = computed(() => [menu.value])

function hasMeta(item, meta) {
  return item.meta && item.meta.includes(meta)
}
function showLogin(item) {
  return !loggedIn.value && hasMeta(item, 'showLogin')
}
function logout() {
  layout.showMobileMenu = false
  $auth.logoutUser()
}
function login() {
  layout.showMobileMenu = false
  $auth.openAuthModal()
}
function liClasses(item) {
  const classes = item.meta || {}
  classes['has-submenu'] = !!item.items
  return classes
}
function linkClasses(item) {
  return {
    all: hasMeta(item, 'all'),
    'en-primeur': hasMeta(item, 'en-primeur'),
  }
}
function getImageUrl(img) {
  return mobileMenuImages[`${img}.svg`]
}
function linkClicked(item) {
  emit('link-clicked', item)
}
watch(menu, (val, oldVal) => {
  if (oldVal) {
    nextTick(() => {
      const menuFocusEl = document.querySelector(
        `[data-button-text="${oldVal.text}"]`,
      )
      if (menuFocusEl) {
        menuFocusEl.focus()
      }
    })
  }
})
</script>

<template>
  <div class="mobile-menu nested-menu">
    <ul v-for="m of menuList" :key="m.text" :class="m.meta">
      <li
        v-for="menuItem of m.items"
        :key="menuItem.text"
        :class="liClasses(menuItem)"
      >
        <!-- if login required, display Sign In button instead -->
        <template v-if="showLogin(menuItem)">
          <button :class="'underline'" @click="login()">
            Sign Up / Log In
          </button>
        </template>
        <template v-else-if="hasMeta(menuItem, 'menu-root-account')">
          <ul>
            <li
              v-for="loggedInMenuItem of menuItem.items"
              :key="loggedInMenuItem.text"
              :class="liClasses(loggedInMenuItem)"
            >
              <template v-if="hasMeta(loggedInMenuItem, 'cellar')">
                <client-only>
                  <MyCellarBadge
                    v-if="$auth.loggedIn"
                    :mobile="true"
                    :mobile-image-src="getImageUrl(loggedInMenuItem.image)"
                  />
                </client-only>
              </template>
              <template v-else-if="hasMeta(loggedInMenuItem, 'orders')">
                <client-only>
                  <MyOrdersBadge
                    v-if="$auth.loggedIn"
                    :mobile="true"
                    :mobile-image-src="getImageUrl(loggedInMenuItem.image)"
                  />
                </client-only>
              </template>
              <template v-if="hasMeta(loggedInMenuItem, 'showLogout')">
                <button
                  class="menu-trigger"
                  :data-button-text="loggedInMenuItem.text"
                  @click="$emit('setActiveMenu', loggedInMenuItem)"
                >
                  <img
                    v-if="loggedInMenuItem.image"
                    :src="getImageUrl(loggedInMenuItem.image)"
                    role="presentation"
                    data-not-lazy
                  />{{ loggedInMenuItem.text }}
                </button>
              </template>
            </li>
          </ul>
        </template>
        <!-- special handler for doesLogout buttons -->
        <button
          v-else-if="hasMeta(menuItem, 'doesLogout')"
          :class="'underline'"
          @click="logout()"
        >
          {{ menuItem.text }}
        </button>
        <!-- If menu has sub-items, this is a trigger -->
        <button
          v-else-if="menuItem.items"
          class="menu-trigger"
          :data-button-text="menuItem.text"
          @click="$emit('setActiveMenu', menuItem)"
        >
          <img
            v-if="menuItem.image"
            :src="getImageUrl(menuItem.image)"
            role="presentation"
            data-not-lazy
          />{{ menuItem.text }}
        </button>
        <!-- If link starts with '/', this is an internal (nuxt) link-->

        <nuxt-link
          v-else-if="menuItem.url && menuItem.url.startsWith('/')"
          event=""
          :class="linkClasses(menuItem)"
          :to="menuItem.url"
          no-prefetch
          @click="linkClicked(menuItem)"
          ><img
            v-if="menuItem.image"
            :src="getImageUrl(menuItem.image)"
            role="presentation"
            data-not-lazy
          />{{ menuItem.text }}</nuxt-link
        >
        <!-- If it has a url this is an external link -->
        <a
          v-else-if="menuItem.url"
          :class="linkClasses(menuItem)"
          :href="menuItem.url"
          >{{ menuItem.text }}</a
        >
        <span v-else-if="menuItem.header" class="header">
          {{ menuItem.text }}
        </span>
        <!-- Otherwise it is a header or some text -->
        <div v-else>{{ menuItem.text }}</div>
        <!-- And it may have internal links that need to be nested in another <ul> -->
        <ul v-if="menuItem.subItems">
          <li
            v-for="subItem of menuItem.subItems"
            :key="subItem.text"
            :class="linkClasses(subItem)"
          >
            <nuxt-link
              event=""
              no-prefetch
              :to="subItem.url"
              @click="linkClicked(subItem)"
              >{{ subItem.text }}
            </nuxt-link>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
